'use client';

import { Box, IconButton, SystemIcon } from '@wishket/design-system';
import { usePreventScroll } from '@wishket/yogokit';
import {
  cloneElement,
  ComponentPropsWithRef,
  PropsWithChildren,
  ReactElement,
  useEffect,
  useState,
} from 'react';
import { twJoin } from 'tailwind-merge';

import { useDeviceCheck } from '@/shared/hooks';
import { Image } from '@/shared/Components';

interface ImageBoxProps {
  src: string;
}

const ImageBox = ({ src, children }: PropsWithChildren<ImageBoxProps>) => {
  const [isZoomedIn, setIsZoomedIn] = useState(false);
  const [isBoxOpened, setIsBoxOpened] = useState(false);
  const { isDesktop } = useDeviceCheck();

  const handleZoomIn = () => {
    setIsBoxOpened(true);
  };

  const handleZoomOut = () => {
    setIsBoxOpened(false);
    setIsZoomedIn(false);
  };

  useEffect(() => {
    if (isBoxOpened) {
      setIsZoomedIn(true);
    }
  }, [isBoxOpened]);

  usePreventScroll(isBoxOpened);

  return (
    <>
      {cloneElement(children as ReactElement<ComponentPropsWithRef<'img'>>, {
        className: 'hover:cursor-zoom-in',
        onClick: handleZoomIn,
      })}
      {isBoxOpened && (
        <Box
          className={twJoin(
            'fixed left-0 top-0 z-50 h-screen w-screen items-center justify-center bg-white',
            isDesktop && 'hover:cursor-zoom-out',
            isBoxOpened ? 'flex' : 'hidden'
          )}
          onClick={() => {
            if (isDesktop) handleZoomOut();
          }}
        >
          <Image
            fill
            src={src || ''}
            alt="본문 이미지"
            priority
            className={twJoin(
              'object-contain transition-transform duration-300 ease-out',
              isZoomedIn ? 'scale-100' : 'scale-0'
            )}
          />
          {!isDesktop && (
            <IconButton
              size="lg"
              outline={false}
              className="absolute right-6 top-6"
              onClick={handleZoomOut}
            >
              <SystemIcon name="large_delete" />
            </IconButton>
          )}
        </Box>
      )}
    </>
  );
};

export default ImageBox;
